import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import { createPortal } from 'react-dom';
import {
  NavLink,
  Navigate,
  Route,
  Routes,
  useNavigate,
} from 'react-router-dom';
import { DateRangePicker } from '@components/DatePicker/DatePicker';
import {
  useBackofficeRolesQuery,
  useBackofficeSeriesExportMutation,
  useBackofficeSeriesRows,
  useBackofficeSession,
  useBackofficeTenantsQuery,
  useBackofficeUsers,
  useCreateTenantMutation,
  useCreateUserMutation,
  useDeleteTenantMutation,
  useDeleteUserMutation,
  useUpdateTenantMutation,
  useUpdateUserMutation,
} from './hooks';
import {
  BackofficePath,
  SERIES_STATUS_ITEMS,
  UNLIMITED_REMAINING_COUNT,
} from './types';
import type {
  CreateTenantBody,
  CreateUserBody,
  SeriesFilters,
  SeriesRow,
  Tenant,
  UpdateUserBody,
  User,
  UserFilters,
} from './types';
import styles from './BackofficePage.module.scss';

type DialogState =
  | { type: 'tenantForm'; tenant?: Tenant }
  | { type: 'tenantDelete'; tenant: Tenant }
  | { type: 'userForm'; user?: User }
  | { type: 'userDelete'; user: User }
  | { type: 'password'; user: User }
  | { type: 'message'; title: string; message: React.ReactNode; tone?: 'success' | 'error' }
  | null;

type ButtonVariant = 'default' | 'primary' | 'danger' | 'ghost';
type DateRangeFilter = {
  startDate: string | null;
  endDate: string | null;
};

type SeriesFilterState = {
  tenantId: string;
  manufacturer: string;
  manufacturerModel: string;
  seriesDateRange: DateRangeFilter;
  createdDateRange: DateRangeFilter;
};

type UserFilterState = {
  tenantId: string;
  userName: string;
};

const REMAINING_COUNT_MIN = 0;
const REMAINING_COUNT_MAX = 10000;
const ACTION_MENU_WIDTH = 176;
const ACTION_MENU_GUTTER = 8;
const USER_STATUS_ACTIVE_ID = '000000000000000000001000';
const EMPTY_DATE_RANGE: DateRangeFilter = {
  startDate: null,
  endDate: null,
};

function cx(...classNames: Array<string | false | null | undefined>) {
  return classNames.filter(Boolean).join(' ');
}

function toErrorMessage(error: unknown, fallback: string) {
  if (isAxiosError<{ message?: string }>(error)) {
    return error.response?.data?.message ?? error.message ?? fallback;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
}

function toExportErrorMessage(error: unknown) {
  if (isAxiosError<{ message?: string }>(error)) {
    const status = error.response?.status ?? error.status;
    if (status === 422) {
      return 'Export failed. The series data is not ready yet. Please try again after a few seconds.';
    }
  }

  return toErrorMessage(error, 'Export failed. Please try again later.');
}

function formatFilterDate(dateValue?: string | null) {
  return dateValue ? dateValue.replaceAll('-', '') : undefined;
}

function toDateRangeFilter(range: DateRangeFilter) {
  const start = formatFilterDate(range.startDate);
  const end = formatFilterDate(range.endDate);

  if (start && end) return `${start}-${end}`;
  return start;
}

function buildSeriesFilters({
  tenantId,
  manufacturer,
  manufacturerModel,
  seriesDateRange,
  createdDateRange,
}: SeriesFilterState): SeriesFilters {
  const filters: SeriesFilters = {};
  const createdAt = toDateRangeFilter(createdDateRange);
  const startDate = formatFilterDate(seriesDateRange.startDate);
  const endDate = formatFilterDate(seriesDateRange.endDate);

  if (tenantId !== 'all') filters.tenantId = tenantId;
  if (manufacturer.trim()) filters.manufacturer = manufacturer.trim();
  if (manufacturerModel.trim()) filters.manufacturerModel = manufacturerModel.trim();
  if (createdAt) filters.createdAt = createdAt;
  if (startDate) filters.startDate = startDate;
  if (endDate) filters.endDate = endDate;

  return filters;
}

function buildUserFilters({ tenantId, userName }: UserFilterState): UserFilters {
  const filters: UserFilters = {};

  if (tenantId !== 'all') filters.tenantId = tenantId;
  if (userName.trim()) filters.user_name = userName.trim();

  return filters;
}

function getUserDisplayName(user: User) {
  return user.name ?? user.user_name ?? '-';
}

function getUserEmail(user: User) {
  return user.user_email ?? user.email ?? '';
}

function getUserTenantId(user?: User) {
  return user?.tenant?.id ?? user?.tenantId ?? '';
}

function formatPasswordMissingRequirements(requirements: string[]) {
  if (requirements.length === 1) return requirements[0];

  return `${requirements.slice(0, -1).join(', ')} and ${
    requirements[requirements.length - 1]
  }`;
}

function getPasswordValidationError(password: string) {
  const missingRequirements: string[] = [];

  if (password.length < 8) missingRequirements.push('at least 8 characters');
  if (!/[A-Z]/.test(password)) missingRequirements.push('an uppercase letter');
  if (!/[a-z]/.test(password)) missingRequirements.push('a lowercase letter');
  if (!/[0-9]/.test(password)) missingRequirements.push('a number');
  if (!/[^A-Za-z0-9]/.test(password)) {
    missingRequirements.push('a special character');
  }

  if (missingRequirements.length === 0) return null;

  return `Password must include ${formatPasswordMissingRequirements(
    missingRequirements,
  )}.`;
}

function Button({
  children,
  variant = 'default',
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
}) {
  const className = cx(
    styles.button,
    styles[`button_${variant}`],
    props.className,
  );

  return (
    <button {...props} className={className} type={props.type ?? 'button'}>
      {children}
    </button>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className={styles.field}>
      <span>{label}</span>
      {children}
    </label>
  );
}

function DateRangeField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: DateRangeFilter;
  onChange: (value: DateRangeFilter) => void;
}) {
  return (
    <div className={styles.field}>
      <span>{label}</span>
      <DateRangePicker value={value} onChange={onChange} />
    </div>
  );
}

function PageTitle({
  title,
  action,
}: {
  title: string;
  action?: React.ReactNode;
}) {
  return (
    <div className={styles.pageTitle}>
      <h1>{title}</h1>
      {action ? <div>{action}</div> : null}
    </div>
  );
}

function BackofficeTabs({ userName }: { userName: string }) {
  const navigate = useNavigate();
  const tabs = [
    { to: BackofficePath.ROOT, label: 'Series', end: true },
    { to: BackofficePath.TENANTS, label: 'Tenants' },
    { to: BackofficePath.USERS, label: 'Users' },
  ];

  return (
    <header className={styles.tabsHeader}>
      <div>
        <div className={styles.brandTitle}>CardioLucid Backoffice</div>
        <div className={styles.brandMeta}>{userName}</div>
      </div>
      <nav className={styles.tabs}>
        {tabs.map((tab) => (
          <NavLink
            key={tab.to}
            end={tab.end}
            to={tab.to}
            className={({ isActive }) =>
              cx(styles.tab, isActive && styles.tab_active)
            }
          >
            {tab.label}
          </NavLink>
        ))}
      </nav>
      <Button variant="ghost" onClick={() => navigate('/browser')}>
        Browser
      </Button>
    </header>
  );
}

function Modal({
  title,
  onClose,
  children,
  size = 'default',
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  size?: 'default' | 'wide' | 'narrow';
}) {
  return (
    <div className={styles.modalBackdrop} onMouseDown={onClose}>
      <div
        className={cx(styles.modalPanel, styles[`modalPanel_${size}`])}
        role="dialog"
        aria-modal="true"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className={styles.modalHeader}>
          <h2>{title}</h2>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ActionMenu({
  label,
  actions,
}: {
  label: string;
  actions: Array<{
    label: string;
    tone?: 'default' | 'danger';
    disabled?: boolean;
    onClick: () => void;
  }>;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  const updateMenuPosition = () => {
    const trigger = buttonRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const maxLeft = window.innerWidth - ACTION_MENU_WIDTH - ACTION_MENU_GUTTER;
    const menuHeight = actions.length * 44;
    const maxTop = window.innerHeight - menuHeight - ACTION_MENU_GUTTER;
    const left = Math.max(
      ACTION_MENU_GUTTER,
      Math.min(rect.right - ACTION_MENU_WIDTH, maxLeft),
    );
    const top = Math.max(
      ACTION_MENU_GUTTER,
      Math.min(rect.bottom + ACTION_MENU_GUTTER, maxTop),
    );

    setMenuPosition({
      top,
      left,
    });
  };

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      setIsOpen(false);
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    const closeMenu = () => setIsOpen(false);

    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('resize', closeMenu);
    window.addEventListener('scroll', closeMenu, true);

    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('resize', closeMenu);
      window.removeEventListener('scroll', closeMenu, true);
    };
  }, [isOpen]);

  return (
    <div className={styles.actionMenu} ref={containerRef}>
      <button
        ref={buttonRef}
        type="button"
        className={styles.actionMenuButton}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={() => {
          if (isOpen) {
            setIsOpen(false);
            return;
          }

          updateMenuPosition();
          setIsOpen(true);
        }}
      >
        ...
      </button>
      {isOpen && menuPosition
        ? createPortal(
            <div
              ref={panelRef}
              className={styles.actionMenuPanel}
              role="menu"
              style={{
                top: menuPosition.top,
                left: menuPosition.left,
              }}
            >
              {actions.map((action) => (
                <button
                  key={action.label}
                  type="button"
                  role="menuitem"
                  disabled={action.disabled}
                  className={cx(
                    styles.actionMenuItem,
                    action.tone === 'danger' && styles.actionMenuItem_danger,
                  )}
                  onClick={() => {
                    setIsOpen(false);
                    action.onClick();
                  }}
                >
                  {action.label}
                </button>
              ))}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

function StatusBadge({ row }: { row: SeriesRow }) {
  const status = SERIES_STATUS_ITEMS.find((item) => item.id === row.statusId);
  const color = status?.color ?? 'gray';

  return (
    <span
      className={cx(styles.statusBadge, styles[`statusBadge_${color}`])}
      title={row.warningMessage ?? undefined}
    >
      {row.isPreventDelete ? 'Locked ' : ''}
      {status?.name ?? '-'}
    </span>
  );
}

function EmptyState({ children }: { children: React.ReactNode }) {
  return <div className={styles.emptyState}>{children}</div>;
}

function SeriesFiltersPanel({
  onApply,
}: {
  onApply: (filters: SeriesFilters) => void;
}) {
  const tenantsQuery = useBackofficeTenantsQuery();
  const [tenantId, setTenantId] = useState('all');
  const [manufacturer, setManufacturer] = useState('');
  const [manufacturerModel, setManufacturerModel] = useState('');
  const [seriesDateRange, setSeriesDateRange] = useState<DateRangeFilter>(
    EMPTY_DATE_RANGE,
  );
  const [createdDateRange, setCreatedDateRange] = useState<DateRangeFilter>(
    EMPTY_DATE_RANGE,
  );

  const getFilters = useCallback(
    () =>
      buildSeriesFilters({
        tenantId,
        manufacturer,
        manufacturerModel,
        seriesDateRange,
        createdDateRange,
      }),
    [
      tenantId,
      manufacturer,
      manufacturerModel,
      seriesDateRange,
      createdDateRange,
    ],
  );

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      onApply(getFilters());
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [getFilters, onApply]);

  const reset = () => {
    setTenantId('all');
    setManufacturer('');
    setManufacturerModel('');
    setSeriesDateRange(EMPTY_DATE_RANGE);
    setCreatedDateRange(EMPTY_DATE_RANGE);
    onApply({});
  };

  return (
    <form
      className={styles.filterBar}
      onSubmit={(event) => {
        event.preventDefault();
        onApply(getFilters());
      }}
    >
      <Field label="Tenant">
        <select value={tenantId} onChange={(event) => setTenantId(event.target.value)}>
          <option value="all">All Tenants</option>
          {(tenantsQuery.data ?? []).map((tenant) => (
            <option key={tenant.id} value={tenant.id}>
              {tenant.name}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Manufacturer">
        <input
          value={manufacturer}
          placeholder="Search Manufacturer"
          onChange={(event) => setManufacturer(event.target.value)}
        />
      </Field>
      <Field label="Model">
        <input
          value={manufacturerModel}
          placeholder="Search Model"
          onChange={(event) => setManufacturerModel(event.target.value)}
        />
      </Field>
      <DateRangeField
        label="Series Date"
        value={seriesDateRange}
        onChange={setSeriesDateRange}
      />
      <DateRangeField
        label="Created Date"
        value={createdDateRange}
        onChange={setCreatedDateRange}
      />
      <div className={styles.filterActions}>
        <Button variant="primary" type="submit">
          Apply Filters
        </Button>
        <Button onClick={reset}>Reset</Button>
      </div>
    </form>
  );
}

function SeriesPage({ openDialog }: { openDialog: (state: DialogState) => void }) {
  const [filters, setFilters] = useState<SeriesFilters>({});
  const seriesQuery = useBackofficeSeriesRows(filters);
  const exportMutation = useBackofficeSeriesExportMutation();
  const rows = seriesQuery.rows;

  const exportSeries = (row: SeriesRow) => {
    exportMutation.mutate(row.id, {
      onSuccess: () => {
        openDialog({
          type: 'message',
          title: 'Success',
          message: 'Download completed successfully.',
          tone: 'success',
        });
      },
      onError: (error) => {
        openDialog({
          type: 'message',
          title: 'Export failed',
          message: toExportErrorMessage(error),
          tone: 'error',
        });
      },
    });
  };

  return (
    <section className={styles.page}>
      <PageTitle title="Series" />
      <SeriesFiltersPanel onApply={setFilters} />
      <div className={styles.tablePanel}>
        {seriesQuery.isError ? (
          <EmptyState>Failed to load series groups.</EmptyState>
        ) : seriesQuery.isPending && rows.length === 0 ? (
          <EmptyState>Loading series groups...</EmptyState>
        ) : rows.length === 0 ? (
          <EmptyState>No series found.</EmptyState>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Status</th>
                <th>Series Date</th>
                <th>Tenant</th>
                <th>Manufacturer</th>
                <th>Model</th>
                <th>Images</th>
                <th>Description</th>
                <th>Created At</th>
                <th>.cl Export</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <StatusBadge row={row} />
                  </td>
                  <td>{row.seriesDate}</td>
                  <td>{row.tenant}</td>
                  <td>{row.manufacturer}</td>
                  <td>{row.model}</td>
                  <td>{row.images}</td>
                  <td className={styles.descriptionCell}>{row.description}</td>
                  <td>{row.createdAt}</td>
                  <td>
                    <Button
                      disabled={exportMutation.isPending}
                      onClick={() => exportSeries(row)}
                    >
                      Export
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className={styles.loadMoreRow}>
          {seriesQuery.isFetchingNextPage ? <span>Loading more series...</span> : null}
          {seriesQuery.hasNextPage ? (
            <Button onClick={() => void seriesQuery.fetchNextPage()}>
              Load more
            </Button>
          ) : null}
        </div>
      </div>
    </section>
  );
}

function TenantForm({
  tenant,
  onCancel,
  onSaved,
}: {
  tenant?: Tenant;
  onCancel: () => void;
  onSaved: (message: string) => void;
}) {
  const createTenant = useCreateTenantMutation();
  const updateTenant = useUpdateTenantMutation();
  const [name, setName] = useState(tenant?.name ?? '');
  const [maxRunnerCount, setMaxRunnerCount] = useState<number | ''>(
    tenant?.maxRunnerCount ?? '',
  );
  const [isUnlimited, setIsUnlimited] = useState(
    tenant?.remainingCount === UNLIMITED_REMAINING_COUNT,
  );
  const [remainingCount, setRemainingCount] = useState<number | ''>(
    tenant?.remainingCount === UNLIMITED_REMAINING_COUNT
      ? UNLIMITED_REMAINING_COUNT
      : tenant?.remainingCount ?? '',
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const currentRemainingCount = isUnlimited
    ? UNLIMITED_REMAINING_COUNT
    : remainingCount;
  const remainingCountOutOfRange =
    !isUnlimited &&
    typeof remainingCount === 'number' &&
    (remainingCount < REMAINING_COUNT_MIN ||
      remainingCount > REMAINING_COUNT_MAX);
  const trimmedName = name.trim();
  const hasRequiredValues =
    trimmedName.length > 0 &&
    typeof maxRunnerCount === 'number' &&
    (isUnlimited || typeof remainingCount === 'number');
  const isDirty =
    !tenant ||
    trimmedName !== tenant.name ||
    maxRunnerCount !== tenant.maxRunnerCount ||
    currentRemainingCount !== tenant.remainingCount;
  const isSubmitting = createTenant.isPending || updateTenant.isPending;
  const isSubmitDisabled =
    !hasRequiredValues || !isDirty || remainingCountOutOfRange || isSubmitting;

  const submit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmitDisabled) return;

    setErrorMessage(null);

    const body: CreateTenantBody = {
      name: trimmedName,
      maxRunnerCount: Number(maxRunnerCount),
      remainingCount: Number(currentRemainingCount),
    };

    if (tenant) {
      updateTenant.mutate(
        { id: tenant.id, body },
        {
          onSuccess: () => onSaved('Tenant updated successfully.'),
          onError: (error) => setErrorMessage(toErrorMessage(error, 'Failed to save tenant.')),
        },
      );
      return;
    }

    createTenant.mutate(body, {
      onSuccess: () => onSaved('Tenant created successfully.'),
      onError: (error) => setErrorMessage(toErrorMessage(error, 'Failed to save tenant.')),
    });
  };

  return (
    <form className={styles.form} onSubmit={submit}>
      <Field label="Tenant Name">
        <input
          value={name}
          disabled={Boolean(tenant)}
          placeholder="Enter tenant name"
          onChange={(event) => setName(event.target.value)}
          required
        />
      </Field>
      <div className={styles.formRow}>
        <Field label="Remaining Count">
          <input
            type="number"
            min={REMAINING_COUNT_MIN}
            max={REMAINING_COUNT_MAX}
            value={isUnlimited ? '' : remainingCount}
            disabled={isUnlimited}
            placeholder="Enter number"
            onChange={(event) => {
              const nextValue = event.target.value.trim();
              setRemainingCount(nextValue === '' ? '' : Number(nextValue));
            }}
            required={!isUnlimited}
          />
        </Field>
        <label className={styles.checkboxField}>
          <input
            type="checkbox"
            checked={isUnlimited}
            onChange={() => {
              setIsUnlimited((current) => {
                const next = !current;
                setRemainingCount(next ? UNLIMITED_REMAINING_COUNT : '');
                return next;
              });
            }}
          />
          Unlimited
        </label>
      </div>
      {remainingCountOutOfRange ? (
        <div className={styles.inlineError}>Value must be between 0 and 10,000.</div>
      ) : null}
      <Field label="Max Runner Count">
        <input
          type="number"
          min={1}
          value={maxRunnerCount}
          placeholder="Enter number"
          onChange={(event) => {
            const nextValue = event.target.value.trim();
            setMaxRunnerCount(nextValue === '' ? '' : Number(nextValue));
          }}
          required
        />
      </Field>
      {errorMessage ? <div className={styles.inlineError}>{errorMessage}</div> : null}
      <div className={styles.modalActions}>
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="primary" type="submit" disabled={isSubmitDisabled}>
          {isSubmitting ? 'Saving...' : tenant ? 'Save Changes' : 'Create Tenant'}
        </Button>
      </div>
    </form>
  );
}

function TenantDeleteDialog({
  tenant,
  onCancel,
  onDeleted,
}: {
  tenant: Tenant;
  onCancel: () => void;
  onDeleted: () => void;
}) {
  const deleteTenant = useDeleteTenantMutation();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  return (
    <div className={styles.dialogBody}>
      <p>
        Are you sure you want to delete tenant <strong>{tenant.name}</strong>?
      </p>
      {errorMessage ? <div className={styles.inlineError}>{errorMessage}</div> : null}
      <div className={styles.modalActions}>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          variant="danger"
          disabled={deleteTenant.isPending}
          onClick={() =>
            deleteTenant.mutate(tenant.id, {
              onSuccess: onDeleted,
              onError: (error) =>
                setErrorMessage(toErrorMessage(error, 'Failed to delete tenant.')),
            })
          }
        >
          Delete Tenant
        </Button>
      </div>
    </div>
  );
}

function TenantsPage({ openDialog }: { openDialog: (state: DialogState) => void }) {
  const tenantsQuery = useBackofficeTenantsQuery();
  const tenants = tenantsQuery.data ?? [];

  return (
    <section className={styles.page}>
      <PageTitle
        title="Tenants"
        action={
          <Button variant="primary" onClick={() => openDialog({ type: 'tenantForm' })}>
            Create Tenant
          </Button>
        }
      />
      <div className={styles.tablePanel}>
        {tenantsQuery.isError ? (
          <EmptyState>Failed to load tenants.</EmptyState>
        ) : tenantsQuery.isPending && tenants.length === 0 ? (
          <EmptyState>Loading tenants...</EmptyState>
        ) : tenants.length === 0 ? (
          <EmptyState>No tenants found.</EmptyState>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Tenant Name</th>
                <th>Used Count</th>
                <th>Remaining Count</th>
                <th>Max Runners</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {tenants.map((tenant) => (
                <tr key={tenant.id}>
                  <td>{tenant.name}</td>
                  <td className={styles.numericCell}>{tenant.usedCount.toLocaleString()}</td>
                  <td className={styles.numericCell}>
                    {tenant.remainingCount === UNLIMITED_REMAINING_COUNT
                      ? 'Unlimited'
                      : tenant.remainingCount.toLocaleString()}
                  </td>
                  <td className={styles.numericCell}>{tenant.maxRunnerCount}</td>
                  <td>
                    <ActionMenu
                      label={`Tenant actions for ${tenant.name}`}
                      actions={[
                        {
                          label: 'Edit Tenant',
                          onClick: () => openDialog({ type: 'tenantForm', tenant }),
                        },
                        {
                          label: 'Delete Tenant',
                          tone: 'danger',
                          onClick: () => openDialog({ type: 'tenantDelete', tenant }),
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

function UserFiltersPanel({
  onApply,
}: {
  onApply: (filters: UserFilters) => void;
}) {
  const tenantsQuery = useBackofficeTenantsQuery();
  const [tenantId, setTenantId] = useState('all');
  const [userName, setUserName] = useState('');

  const getFilters = useCallback(
    () =>
      buildUserFilters({
        tenantId,
        userName,
      }),
    [tenantId, userName],
  );

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      onApply(getFilters());
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [getFilters, onApply]);

  const reset = () => {
    setTenantId('all');
    setUserName('');
    onApply({});
  };

  return (
    <form
      className={styles.filterBarCompact}
      onSubmit={(event) => {
        event.preventDefault();
        onApply(getFilters());
      }}
    >
      <Field label="Tenant">
        <select value={tenantId} onChange={(event) => setTenantId(event.target.value)}>
          <option value="all">All Tenants</option>
          {(tenantsQuery.data ?? []).map((tenant) => (
            <option key={tenant.id} value={tenant.id}>
              {tenant.name}
            </option>
          ))}
        </select>
      </Field>
      <Field label="User ID">
        <input
          value={userName}
          onChange={(event) => setUserName(event.target.value)}
        />
      </Field>
      <div className={styles.filterActions}>
        <Button variant="primary" type="submit">
          Apply Filters
        </Button>
        <Button onClick={reset}>Reset</Button>
      </div>
    </form>
  );
}

function UserForm({
  user,
  onCancel,
  onSaved,
}: {
  user?: User;
  onCancel: () => void;
  onSaved: (message: string) => void;
}) {
  const createUser = useCreateUserMutation();
  const updateUser = useUpdateUserMutation();
  const tenantsQuery = useBackofficeTenantsQuery();
  const rolesQuery = useBackofficeRolesQuery();
  const originalTenantId = getUserTenantId(user);
  const [userName, setUserName] = useState(user?.user_name ?? user?.name ?? '');
  const [email, setEmail] = useState(user ? getUserEmail(user) : '');
  // const [firstName, setFirstName] = useState(user?.firstName ?? '');
  // const [lastName, setLastName] = useState(user?.lastName ?? '');
  const [password, setPassword] = useState('');
  const [tenantId, setTenantId] = useState(originalTenantId);
  const [roleId, setRoleId] = useState(user?.role?.id ?? '');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const passwordValidationError =
    !user && password.length > 0 ? getPasswordValidationError(password) : null;
  const isSubmitting = createUser.isPending || updateUser.isPending;
  const hasRequiredValues =
    userName.trim().length > 0 &&
    email.trim().length > 0 &&
    tenantId.length > 0 &&
    roleId.length > 0 &&
    (Boolean(user) || (password.length > 0 && !passwordValidationError));
  const isDirty =
    !user ||
    email.trim() !== getUserEmail(user) ||
    tenantId !== originalTenantId ||
    roleId !== (user.role?.id ?? '');
  const isSubmitDisabled =
    !hasRequiredValues || !isDirty || isSubmitting;

  const submit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmitDisabled) return;

    setErrorMessage(null);

    if (!user) {
      const body: CreateUserBody = {
        name: userName.trim(),
        email: email.trim(),
        password,
        isGuest: true,
        userStatus: { id: USER_STATUS_ACTIVE_ID },
        role: { id: roleId },
        tenantId,
        note: '',
      };

      createUser.mutate(body, {
        onSuccess: () => onSaved('User created successfully.'),
        onError: (error) => setErrorMessage(toErrorMessage(error, 'Failed to save user.')),
      });
      return;
    }

    const body: UpdateUserBody = {};

    if (email.trim() !== getUserEmail(user)) body.email = email.trim();
    if (tenantId !== originalTenantId) body.tenantId = tenantId;
    if (roleId !== (user.role?.id ?? '')) body.role = { id: roleId };
    console.log(tenantId,body)
    updateUser.mutate(
      { id: user.id, body },
      {
        onSuccess: () => onSaved('User updated successfully.'),
        onError: (error) => setErrorMessage(toErrorMessage(error, 'Failed to save user.')),
      },
    );
  };

  return (
    <form className={styles.form} onSubmit={submit}>
      <Field label="User ID">
        <input
          value={userName}
          disabled={Boolean(user)}
          placeholder="Enter User ID"
          onChange={(event) => setUserName(event.target.value)}
          required
        />
      </Field>
      <Field label="Email">
        <input
          type="email"
          value={email}
          placeholder="Enter email"
          onChange={(event) => setEmail(event.target.value)}
          required
        />
      </Field>
{/*       <div className={styles.formRow}>
        <Field label="First Name">
          <input
            value={firstName}
            placeholder="Enter first name"
            onChange={(event) => setFirstName(event.target.value)}
            required
          />
        </Field>
        <Field label="Last Name">
          <input
            value={lastName}
            placeholder="Enter last name"
            onChange={(event) => setLastName(event.target.value)}
            required
          />
        </Field>
      </div> */}
      {!user ? (
        <Field label="Password">
          <input
            type="password"
            value={password}
            placeholder="Enter password"
            onChange={(event) => setPassword(event.target.value)}
            required
          />
        </Field>
      ) : null}
      {passwordValidationError ? (
        <div className={styles.inlineError}>{passwordValidationError}</div>
      ) : null}
      <Field label="Tenant">
        <select
          value={tenantId}
          disabled={tenantsQuery.isPending}
          onChange={(event) => setTenantId(event.target.value)}
          required
        >
          <option value="">Select tenant</option>
          {(tenantsQuery.data ?? []).map((tenant) => (
            <option key={tenant.id} value={tenant.id}>
              {tenant.name}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Role">
        <select
          value={roleId}
          disabled={rolesQuery.isPending}
          onChange={(event) => setRoleId(event.target.value)}
          required
        >
          <option value="">Select role</option>
          {(rolesQuery.data ?? []).map((role) => (
            <option key={role.id} value={role.id}>
              {role.name}
            </option>
          ))}
        </select>
      </Field>
      {errorMessage ? <div className={styles.inlineError}>{errorMessage}</div> : null}
      <div className={styles.modalActions}>
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="primary" type="submit" disabled={isSubmitDisabled}>
          {isSubmitting ? 'Saving...' : user ? 'Save Changes' : 'Create User'}
        </Button>
      </div>
    </form>
  );
}

function PasswordDialog({
  user,
  onCancel,
  onSaved,
}: {
  user: User;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const updateUser = useUpdateUserMutation();
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const isPasswordTooShort = password.length > 0 && password.length < 8;
  const isSubmitDisabled =
    password.length === 0 || isPasswordTooShort || updateUser.isPending;

  return (
    <form
      className={styles.form}
      onSubmit={(event) => {
        event.preventDefault();
        if (isSubmitDisabled) return;

        updateUser.mutate(
          { id: user.id, body: { password } },
          {
            onSuccess: onSaved,
            onError: (error) =>
              setErrorMessage(toErrorMessage(error, 'Failed to reset password.')),
          },
        );
      }}
    >
      <p>Reset password for <strong>{getUserDisplayName(user)}</strong>.</p>
      <Field label="Password">
        <input
          type="password"
          value={password}
          placeholder="Enter password"
          onChange={(event) => setPassword(event.target.value)}
          required
        />
      </Field>
      {isPasswordTooShort ? (
        <div className={styles.inlineError}>Password must be at least 8 characters.</div>
      ) : null}
      {errorMessage ? <div className={styles.inlineError}>{errorMessage}</div> : null}
      <div className={styles.modalActions}>
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="danger" type="submit" disabled={isSubmitDisabled}>
          Reset Password
        </Button>
      </div>
    </form>
  );
}

function UserDeleteDialog({
  user,
  onCancel,
  onDeleted,
}: {
  user: User;
  onCancel: () => void;
  onDeleted: () => void;
}) {
  const deleteUser = useDeleteUserMutation();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  return (
    <div className={styles.dialogBody}>
      <p>
        Are you sure you want to delete user <strong>{getUserDisplayName(user)}</strong>?
      </p>
      {errorMessage ? <div className={styles.inlineError}>{errorMessage}</div> : null}
      <div className={styles.modalActions}>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          variant="danger"
          disabled={deleteUser.isPending}
          onClick={() =>
            deleteUser.mutate(user.id, {
              onSuccess: onDeleted,
              onError: (error) =>
                setErrorMessage(toErrorMessage(error, 'Failed to delete user.')),
            })
          }
        >
          Delete User
        </Button>
      </div>
    </div>
  );
}

function UsersPage({ openDialog }: { openDialog: (state: DialogState) => void }) {
  const [filters, setFilters] = useState<UserFilters>({});
  const usersQuery = useBackofficeUsers(filters);
  const tenantsQuery = useBackofficeTenantsQuery();
  const users = usersQuery.users;
  const tenantNameById = useMemo(
    () =>
      Object.fromEntries(
        (tenantsQuery.data ?? []).map((tenant) => [tenant.id, tenant.name]),
      ),
    [tenantsQuery.data],
  );

  return (
    <section className={styles.page}>
      <PageTitle
        title="Users"
        action={
          <Button variant="primary" onClick={() => openDialog({ type: 'userForm' })}>
            Create User
          </Button>
        }
      />
      <UserFiltersPanel onApply={setFilters} />
      <div className={styles.tablePanel}>
        {usersQuery.isError ? (
          <EmptyState>Failed to load users.</EmptyState>
        ) : usersQuery.isPending && users.length === 0 ? (
          <EmptyState>Loading users...</EmptyState>
        ) : users.length === 0 ? (
          <EmptyState>No users found.</EmptyState>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>ID</th>
                <th>Tenant</th>
                <th>Role</th>
                <th>Created At</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>{getUserDisplayName(user)}</td>
                  <td>
                    {user.tenant?.name ??
                      (user.tenantId ? tenantNameById[user.tenantId] : undefined) ??
                      user.tenantId ??
                      '-'}
                  </td>
                  <td>{user.role?.name ?? '-'}</td>
                  <td>
                    {user.createdAt
                      ? new Date(user.createdAt).toLocaleDateString()
                      : '-'}
                  </td>
                  <td>
                    <ActionMenu
                      label={`User actions for ${getUserDisplayName(user)}`}
                      actions={[
                        {
                          label: 'Edit User',
                          onClick: () => openDialog({ type: 'userForm', user }),
                        },
                        {
                          label: 'Reset Password',
                          onClick: () => openDialog({ type: 'password', user }),
                        },
                        {
                          label: 'Delete User',
                          tone: 'danger',
                          onClick: () => openDialog({ type: 'userDelete', user }),
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className={styles.loadMoreRow}>
          {usersQuery.isFetchingNextPage ? <span>Loading more...</span> : null}
          {usersQuery.hasNextPage ? (
            <Button onClick={() => void usersQuery.fetchNextPage()}>
              Load more
            </Button>
          ) : null}
        </div>
      </div>
    </section>
  );
}

function BackofficeDialog({
  dialog,
  closeDialog,
  openDialog,
}: {
  dialog: DialogState;
  closeDialog: () => void;
  openDialog: (state: DialogState) => void;
}) {
  if (!dialog) return null;

  if (dialog.type === 'tenantForm') {
    return (
      <Modal title={dialog.tenant ? 'Edit Tenant' : 'Create Tenant'} onClose={closeDialog}>
        <TenantForm
          tenant={dialog.tenant}
          onCancel={closeDialog}
          onSaved={(message) =>
            openDialog({ type: 'message', title: 'Success', message, tone: 'success' })
          }
        />
      </Modal>
    );
  }

  if (dialog.type === 'tenantDelete') {
    return (
      <Modal title="Delete Tenant" onClose={closeDialog} size="narrow">
        <TenantDeleteDialog
          tenant={dialog.tenant}
          onCancel={closeDialog}
          onDeleted={() =>
            openDialog({
              type: 'message',
              title: 'Success',
              message: 'Tenant deleted successfully.',
              tone: 'success',
            })
          }
        />
      </Modal>
    );
  }

  if (dialog.type === 'userForm') {
    return (
      <Modal title={dialog.user ? 'Edit User' : 'Create User'} onClose={closeDialog}>
        <UserForm
          user={dialog.user}
          onCancel={closeDialog}
          onSaved={(message) =>
            openDialog({ type: 'message', title: 'Success', message, tone: 'success' })
          }
        />
      </Modal>
    );
  }

  if (dialog.type === 'password') {
    return (
      <Modal title="Reset Password" onClose={closeDialog} size="narrow">
        <PasswordDialog
          user={dialog.user}
          onCancel={closeDialog}
          onSaved={() =>
            openDialog({
              type: 'message',
              title: 'Success',
              message: 'Password reset successfully.',
              tone: 'success',
            })
          }
        />
      </Modal>
    );
  }

  if (dialog.type === 'userDelete') {
    return (
      <Modal title="Delete User" onClose={closeDialog} size="narrow">
        <UserDeleteDialog
          user={dialog.user}
          onCancel={closeDialog}
          onDeleted={() =>
            openDialog({
              type: 'message',
              title: 'Success',
              message: 'User deleted successfully.',
              tone: 'success',
            })
          }
        />
      </Modal>
    );
  }

  return (
    <Modal title={dialog.title} onClose={closeDialog} size="narrow">
      <div className={styles.dialogBody}>
        <div className={cx(styles.notice, dialog.tone === 'error' && styles.notice_error)}>
          {dialog.message}
        </div>
        <div className={styles.modalActions}>
          <Button variant="primary" onClick={closeDialog}>
            OK
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default function BackofficePage() {
  const { user, isAdmin, isAuthenticated } = useBackofficeSession();
  const [dialog, setDialog] = useState<DialogState>(null);

  if (!isAuthenticated) {
    return <Navigate replace to="/" />;
  }

  if (!isAdmin) {
    return (
      <main className={styles.backoffice}>
        <EmptyState>Administrator permission is required to access Backoffice.</EmptyState>
      </main>
    );
  }

  return (
    <main className={styles.backoffice}>
      <BackofficeTabs userName={user?.name ?? user?.user_name ?? 'Admin'} />
      <section className={styles.content}>
        <Routes>
          <Route index element={<SeriesPage openDialog={setDialog} />} />
          <Route path="tenants" element={<TenantsPage openDialog={setDialog} />} />
          <Route path="users" element={<UsersPage openDialog={setDialog} />} />
          <Route path="*" element={<Navigate replace to={BackofficePath.ROOT} />} />
        </Routes>
      </section>
      <BackofficeDialog
        dialog={dialog}
        closeDialog={() => setDialog(null)}
        openDialog={setDialog}
      />
    </main>
  );
}
